import { httpApi } from '@api/http.api.ts';
import type {
    Pathway,
    PathwaySummary,
    PathwayWatchlistRequest,
    StatisticsParams,
} from '@routes/sidebar/admin-center/___shared___/types.ts';
import type { EmptyResultsDescription, PaginatedResponse } from '@types';

export interface WatchlistVesselPathwayStatistics extends PathwaySummary {
    vesselCrewCount: number;
}

export interface SummaryCrewUser {
    userId: number;
    name: string;
    lastActivityUTC: string | null;
    rank: string;
    isPathwayAssigned: boolean;
    progressPercent: number | null;
    dueUTC: string | null;
    completedAtUTC: string | null;
    validForDays: number | null;
    dueInDays: number | null;
    overdueForDays: number | null;
}

export interface SummaryCrewUsersParams extends StatisticsParams {
    pathwayId: number;
    vesselId: number;
    payload: PathwayWatchlistRequest;
}

export interface WatchlistVesselParams extends StatisticsParams {
    vesselId: number;
    payload: PathwayWatchlistRequest;
}

export interface VesselPathwaysParams extends StatisticsParams {
    vesselId: number;
}

interface CommonRequestParams {
    tenantAlias: string;
    vesselId: number;
    pathwayId: number;
}

interface UpdateOrderRequestParams extends CommonRequestParams {
    order: number;
    isDeleted: boolean;
}

const getWatchlistVesselBaseUrl = (tenantAlias: string, vesselId: number) =>
    `admin-center/tenants/${tenantAlias}/statistics/watchlists/vessels/${vesselId}`;

export const getApiVesselPathways = ({ tenantAlias, vesselId }: VesselPathwaysParams): Promise<Pathway[]> =>
    httpApi.get<Pathway[]>(`${getWatchlistVesselBaseUrl(tenantAlias, vesselId)}/pathways`).then(({ data }) => data);

export const getApiWatchlistVessel = ({
    tenantAlias,
    vesselId,
    payload,
}: WatchlistVesselParams): Promise<PaginatedResponse<WatchlistVesselPathwayStatistics, EmptyResultsDescription>> =>
    httpApi
        .post<
            PaginatedResponse<WatchlistVesselPathwayStatistics, EmptyResultsDescription>
        >(`${getWatchlistVesselBaseUrl(tenantAlias, vesselId)}/search`, payload)
        .then(({ data }) => data);

export const postApiPathwayToVesselWatchlist = ({
    tenantAlias,
    vesselId,
    pathwayId,
}: CommonRequestParams): Promise<any> =>
    httpApi.post(`${getWatchlistVesselBaseUrl(tenantAlias, vesselId)}`, { pathwayId }).then(({ data }) => data);

export const updateApiVesselWatchlistPathwayOrder = ({
    tenantAlias,
    vesselId,
    pathwayId,
    order,
    isDeleted,
}: UpdateOrderRequestParams): Promise<any> =>
    httpApi
        .put(`${getWatchlistVesselBaseUrl(tenantAlias, vesselId)}`, {
            pathwayId,
            order,
            isDeleted,
        })
        .then(({ data }) => data);

export const getApiSummaryCrewUsers = ({
    tenantAlias,
    pathwayId,
    vesselId,
    payload,
}: SummaryCrewUsersParams): Promise<PaginatedResponse<SummaryCrewUser, number>> =>
    httpApi
        .post<
            PaginatedResponse<SummaryCrewUser, number>
        >(`${getWatchlistVesselBaseUrl(tenantAlias, vesselId)}/pathways/${pathwayId}/summary-crew-users/search`, payload)
        .then(({ data }) => data);
